import { useAuth0 } from "@auth0/auth0-react";
import CrossSvg from "assets/icons/svg/cross";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import AlertDialog from "components/Dialog/index";
import MyButton from "components/MyButton";
import NewDialog from "components/NewDialog/Dialog";
import { useFormik } from "formik";
import { FormikWizard } from "formik-wizard-form";
import { getCurrency } from "helper/numberFormat";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import thousands from "thousands";
import { emailValidation } from "utils/validation";
import FinanceService from "../../../Lib/API/Finance";
import { DateFormetContext } from "../../../app/App.js";
import {
  RecurringModelCheck,
  SetRecurringChecks,
} from "../../../app/redux/actions/index";
import UserContext from "../NewChargeContext";
import ChargeDetails from "./ChargeDetails";
import PaymentInformation from "./PaymentInformation";
import Review from "./Review";
import NewChargeSchema from "./validationSchema";

const NewCharge = ({
  setStepOne,
  setStepper,
  activeIndex,
  handleClose,
  data,
  setData,
  setBatchList,
  batchList,
  account,
  ...props
}) => {
  const RecurringModel = useSelector(
    (state) => state.homeReducer.RecurringModelCheck
  );
  const dateFormet = React.useContext(DateFormetContext);
  const footerPortalRef = useRef(null);
  const [footerPortalReady, setFooterPortalReady] = useState(false);
  const [activeStepForHeight, setActiveStepForHeight] = useState(0);
  const [maxContentHeight, setMaxContentHeight] = useState(0);
  const [minHeightCap, setMinHeightCap] = useState(Infinity);
  const newChargeRef = useRef(null);
  const stepContentRef = useRef(null);
  const reviewMeasureRef = useRef(null);
  const [/* finalValues */ setFinalValues] = useState({});
  const { hfinish, setHFinish } = useContext(UserContext);
  const [title, setTitle] = useState("New Transaction");
  const [transactionIds, setTransactionIds] = useState([]);
  const [TransactionId, setTransactionId] = useState(null);
  const [chargeId, setChargeId] = useState("");
  const [chargeIds, setChargeIds] = useState([]);
  const [paidAmount, setPaidAmount] = useState(false);
  const [inputValue, setInputValues] = useState("");
  const [disable, setDisabled] = useState(true);
  const [creditOnAccountAmount, setCreditOnAccountAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [btnContent, setBtnContent] = useState("llll");
  const { getAccessTokenSilently, user: auth0User } = useAuth0();

  const savePaymentHistory = (values) => {
    try {
      const adminKey = auth0User?.sub || auth0User?.email;
      if (!adminKey) return;
      const types = [];
      if (parseFloat(values?.creditCardAmount) > 0) types.push("credit card");
      if (parseFloat(values?.ach) > 0) types.push("ach");
      if (parseFloat(values?.cash_payment) > 0) types.push("cash");
      if (parseFloat(values?.onAccountCredit_payment) > 0) types.push("credit on account");
      if (values?.checks?.some(c => parseFloat(c?.amount) > 0)) types.push("check");
      // custom payment methods via _payment keys
      Object.entries(values || {}).forEach(([key, val]) => {
        if (key.includes("_payment") && !["cash_payment", "onAccountCredit_payment"].includes(key) && parseFloat(val) > 0) {
          const name = key.replace("_payment", "").toLowerCase();
          if (!types.includes(name)) types.push(name);
        }
      });
      if (!types.length) return;
      const key = `admin_payment_history_${adminKey}`;
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      const merged = [
        ...types,
        ...existing.filter(t => !types.includes(t)),
      ].slice(0, 3);
      localStorage.setItem(key, JSON.stringify(merged));
    } catch (_) {}
  };

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openConfirmNewPaymentModal, setOpenConfirmNewPaymentModal] = useState(false);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);

  const currency = getCurrency();

  const confirmPayment = useRef(false);
  const checkoutInFlight = useRef(false);
  const checkSplit = (is_splitted) => {
    return is_splitted === true;
  };
  const splittedArray = data?.allData?.map((item) => item.is_splitted);
  const getSplittedArray = splittedArray && splittedArray?.every(checkSplit);
  const HidePaymentButton = useSelector(
    (state) => state.homeReducer.hidePaymentButton
  );
  const [openModalDialog, setOpenModalDialog] = React.useState(false);
  const [resTransactionId, setResTransId] = useState(null);
  const { accountTransaction } = useSelector((state) => state.CommonStates);

  const dispatch = useDispatch();

  useEffect(() => {
    if (RecurringModel === true) {
      onDeleteClick();
    }
  }, [RecurringModel]);

  useLayoutEffect(() => {
    let scroller = stepContentRef.current?.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
      scroller = scroller.parentElement;
    }
    if (scroller) scroller.scrollTop = 0;
  }, [activeStepForHeight]);

  useLayoutEffect(() => {
    // Only Payment Information is measured here (Review is measured through
    // its hidden clone below), so the tall Transaction Details step doesn't
    // inflate the min-height applied to steps 2 and 3.
    if (activeStepForHeight !== 1) return;
    const el = stepContentRef.current;
    if (!el) return;
    // Synchronous snapshot of whichever step just became active, taken
    // before paint, so a step that's the first one ever rendered (e.g. Pay
    // Now opens straight into Payment Information) is sized correctly on
    // the very first paint instead of flashing a smaller height until the
    // async ResizeObserver below catches up.
    const height = el.getBoundingClientRect().height;
    if (height) {
      setMaxContentHeight((prev) => (height > prev ? height : prev));
    }
  }, [activeStepForHeight]);

  useEffect(() => {
    if (activeStepForHeight !== 1) return;
    const el = stepContentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const height = entries?.[0]?.contentRect?.height;
      if (height) {
        setMaxContentHeight((prev) => (height > prev ? height : prev));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeStepForHeight]);

  useLayoutEffect(() => {
    // Review is pre-rendered off-screen (see the hidden clone in the JSX
    // below) at its default, collapsed-accordion height as soon as the
    // modal mounts, regardless of which step is shown first. This lets us
    // know Review's real height in advance, so stepper 2 can match stepper
    // 3 even the very first time the wizard is opened directly onto step 2
    // (e.g. via Pay Now), instead of only learning Review's height after
    // the user visits it once.
    const el = reviewMeasureRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const height = entries?.[0]?.contentRect?.height;
      if (height) {
        setMaxContentHeight((prev) => (height > prev ? height : prev));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const stepMinHeight =
    activeStepForHeight === 1 || activeStepForHeight === 2
      ? Math.min(Math.max(maxContentHeight + 220, 710), minHeightCap)
      : undefined;

  useLayoutEffect(() => {
    // Trim the equal-height padding by exactly the amount that makes the
    // dialog scroll, never cutting into the step's real content.
    const root = newChargeRef.current;
    if (!root || stepMinHeight === undefined) return;
    let scroller = root.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    const overflow = scroller.scrollHeight - scroller.clientHeight;
    const contentHeight = [...root.children].reduce(
      (sum, child) => sum + child.getBoundingClientRect().height,
      0
    );
    const padding = stepMinHeight - contentHeight;
    if (overflow > 0 && padding > 0) {
      setMinHeightCap(stepMinHeight - Math.min(overflow, padding));
    }
  }, [stepMinHeight]);

  useEffect(() => {
    const resetCap = () => setMinHeightCap(Infinity);
    window.addEventListener("resize", resetCap);
    return () => window.removeEventListener("resize", resetCap);
  }, []);

  const onDeleteClick = async () => {
    setOpenModalDialog(true);
  };

  const handleSubmit = async () => {
    const token = await getAccessTokenSilently();
    setLoading(true);

    await FinanceService.deleteTransaction(resTransactionId, token)
      .then((res) => {
        if (res) {
          toastr.success("Transaction deleted successfully");
          setTimeout(() => {
            setLoading(false);
            dispatch(RecurringModelCheck(false));
            setOpenModalDialog(false);
            handleClose();
          }, 1500);
        }
      })
      .catch((err) => {
        setLoading(false);
        // toastr.error(err?.data?.message);
        dispatch(RecurringModelCheck(false));
        setOpenModalDialog(false);
        handleClose();
      });
  };

  const formik = useFormik({
    isInitialValid: false,
    enableReinitialize: true,
    initialValues: {},
    validationSchema: "",
    onSubmit: async (values) => { },
  });

  const sumOfAmounts = data?.allData?.reduce((a, b) => {
    return a + b?.Charges?.amount_due;
  }, 0);

  const original_amount = data?.discount
    ? data?.discount_type === "%"
      ? data?.discount === 100
        ? data?.originalAmount
        : data?.total_amount / (1 - data?.discount / 100)
      : data?.total_amount + data?.discount
    : data?.total_amount;

  const initialValues = data?.hasOwnProperty("_id")
    ? {
      values: [
        {
          Ledger: data?.Ledger ?? {},
          ChargeStatus: data?.charge?.payment?.status,
          SubLedger:
            data.SubLedger ?? formik.values?.values?.[0]?.SubLedger?.id,
          discount: data?.discount ?? 0,
          discount_type: data?.allData?.[0]?.discount_type
            ? data?.allData?.[0]?.discount_type
            : data?.discount_type ?? "%",
          due_date: data?.charge?.due_date,
          charge: data?.charge,
          is_splitted: data?.is_splitted ?? false,
          isRecurring: data?.isRecurring ?? false,
          recurring_duration:
            data?.recurringDetails?.frequency === "month"
              ? {
                name: "Monthly",
                id: "month",
                number: 1,
              }
              : data?.recurringDetails?.frequency === "quarter"
                ? {
                  name: "Every 3 months",
                  id: "quarter",
                  number: 3,
                }
                : data?.recurringDetails?.frequency === "halfYear"
                  ? {
                    name: "Every 6 months",
                    id: "halfYear",
                    number: 6,
                  }
                  : {
                    name: "Yearly",
                    id: "year",
                    number: 12,
                  },
          // recurringDetails: data?.recurringDetails,
          recurringStart: data?.recurringDetails?.recurringStart,
          recurringEnd: data?.recurringDetails?.recurringEnd,
          // recurring_duration: data.recurringDetails?.frequency,
          cycle: data?.recurringDetails?.cycle || {},
          payment_date: data.createdAt,
          tax: data.tax_deductible ?? 0,
          tax_type: data.tax_type ?? "%",
          charge_type: data.charge_type,
          total_amount: data.charge_type === "donation" && data.originalAmount ? data.originalAmount : data.total_amount,
          split_duration: data?.split_duration,
          split_amount: data?.charge?.amount,
          number_of_split: data?.number_of_split,
          note:
            data?.comment_invoice?.note != undefined ||
              data?.comment_invoice?.note != ""
              ? data?.comment_invoice?.note
              : data?.allData?.[0]?.comment_invoice?.note != undefined ||
                data?.allData?.[0]?.comment_invoice?.note != ""
                ? data?.allData?.[0]?.comment_invoice?.note
                : formik?.values?.allData?.[0]?.comment_invoice?.note,
          comment_checkbox: data?.comment_invoice?.visible
            ? data?.comment_invoice?.visible
            : data?.allData?.[0]?.comment_invoice?.visible
              ? data?.allData?.[0]?.comment_invoice?.visible
              : formik?.values?.allData?.[0]?.comment_invoice?.visible,

          donation_donorName: data?.charge_type === "donation" ? data?.tribute_info?.donorName || "" : undefined,
          applyTributeInfo: data?.tribute_info?.type ? true : false,
          tribute_type: data?.tribute_info?.type ? {
            name: data?.tribute_info?.type
          } : undefined, donor_name: data?.tribute_info?.name ? data?.tribute_info?.name : undefined,
          message: data?.tribute_info?.message ? data?.tribute_info?.message : undefined,
          showAcknowledgement: data?.tribute_info?.acknowledgment ? true : false,
          acknowledgements: data?.tribute_info?.recipients?.length ? data?.tribute_info?.recipients?.map((item) => {
            return {
              email: item.email,
              recipient_name: item.recipient_name
            }
          }
          )
            : undefined,

          isInstallmentTransaction: data?.isInstallmentTransaction ?? false,
          installmentDetailsTransaction: data?.isInstallmentTransaction ? {
            numberOfInstallment: data?.installmentDetailsTransaction?.numberOfInstallment,
            lastInstallmentDate: data?.installmentDetailsTransaction?.lastInstallmentDate || "",
          } : {},
        },
      ],
      comment_checkbox: data?.comment_invoice?.visible
        ? data?.comment_invoice?.visible
        : data?.allData?.[0]?.comment_invoice?.visible
          ? data?.allData?.[0]?.comment_invoice?.visible
          : formik?.values?.allData?.[0]?.comment_invoice?.visible,
      note:
        data?.comment_invoice?.note != undefined ||
          data?.comment_invoice?.note != ""
          ? data?.comment_invoice?.note
          : data?.allData?.[0]?.comment_invoice?.note != undefined ||
            data?.allData?.[0]?.comment_invoice?.note != ""
            ? data?.allData?.[0]?.comment_invoice?.note
            : formik?.values?.allData?.[0]?.comment_invoice?.note,
      actual_total: data?.charge_type === "donation" && data?.originalAmount ? data.originalAmount : data?.discount > 0 ? original_amount?.toFixed(2) : data.total_amount,
      totalPaid: data?.total_paid,
      current_amount: data?.charge_type === "donation" && data?.originalAmount ? data.originalAmount : data?.discount > 0 ? original_amount?.toFixed(2) : data.total_amount,
      account_name: data.Account,
      isTransactionSave: true,
      Ledger: data?.Ledger ?? {},
      SubLedger: data.SubLedger ?? formik.values?.values?.[0]?.SubLedger?.id,
      checks: [],
      type: data.type,
      amount_due: data?.Charges?.amount_due ?? data?.charge?.amount_due,
      transaction_id: data?._id,
      charge_id: data?.charge?._id,
      receipt_note: data?.comment_receipt?.note,
      comment_receipt: data?.comment_receipt?.visible,
      paymentDate: new Date(),
      allowCreditInstallment: data?.allowInstallment ?? false,
      installmentCreditDetails: {
        cycle: data?.installmentDetails?.cycle ?? "",
        frequency: data?.installmentDetails?.frequency ?? "",
        startInstallment: data?.installmentDetails?.startInstallment ?? new Date(),
      },
      allowAchInstallment: data?.allowInstallment ?? false,
      installmentAchDetails: {
        cycle: data?.installmentDetails?.cycle ?? "",
        frequency: data?.installmentDetails?.frequency ?? "",
        startInstallment: data?.installmentDetails?.startInstallment ?? new Date(),
      },
      emailOnReceipt: false,
      emailsReceipt: [],
      accountInfo: account ?? {},
    }
    : data?.allData
      ? {
        allData: data?.allData,
        chargesID: data?.allData?.map((item) => {
          return item?._id;
        }),
        accountIds: data?.allData[0]?.Account?.id,

        actual_total: sumOfAmounts,
        current_amount: sumOfAmounts,
        account_name: data.Account,
        isTransactionSave: true,
        receipt_note: data?.comment_receipt?.note,
        comment_receipt: data?.comment_receipt?.visible,
        note: data?.allData?.[0]?.comment_invoice?.note,
        transaction_id: data?._id,
        charge_id: data?.charge?._id,
        comment_checkbox: data?.comment_invoice?.visible
          ? data?.comment_invoice?.visible
          : data?.allData?.[0]?.comment_invoice?.visible
            ? data?.allData?.[0]?.comment_invoice?.visible
            : formik?.values?.allData?.[0]?.comment_invoice?.visible,
        emailOnReceipt: false,
        emailsReceipt: [],
        // note: data?.comment_invoice?.note,
        // comment_checkbox: data?.comment_invoice?.visible,
        checks: [],
        values: [
          {
            Ledger: data.Ledger ?? formik.values?.values?.[0]?.Ledger?.id,
            SubLedger:
              data.SubLedger ?? formik.values?.values?.[0]?.SubLedger?.id,
            discount: data?.discount ?? 0,
            discount_type: data?.allData?.[0]?.discount_type
              ? data?.allData?.[0]?.discount_type
              : data?.discount_type ?? "%",
            due_date: data?.charge?.due_date,
            charge: data?.charge,
            is_splitted: data?.is_splitted ?? false,
            isRecurring: data?.isRecurring ?? false,
            recurring_duration:
              data?.recurringDetails?.frequency === "month"
                ? {
                  name: "Monthly",
                  id: "month",
                  number: 1,
                }
                : data?.recurringDetails?.frequency === "quarter"
                  ? {
                    name: "Every 3 months",
                    id: "quarter",
                    number: 3,
                  }
                  : data?.recurringDetails?.frequency === "halfYear"
                    ? {
                      name: "Every 6 months",
                      id: "halfYear",
                      number: 6,
                    }
                    : {
                      name: "Yearly",
                      id: "year",
                      number: 12,
                    },
            // recurringDetails: data?.recurringDetails,
            recurringStart: data?.recurringDetails?.recurringStart,
            recurringEnd: data?.recurringDetails?.recurringEnd,
            // recurring_duration: data.recurringDetails?.frequency,
            cycle: data?.recurringDetails?.cycle || {},
            payment_date: data.createdAt,
            tax: data.tax ?? 0,
            tax_type: data.tax_type ?? "%",
            charge_type: data.charge_type,
            total_amount: data.total_amount,
            split_duration: data?.split_duration,
            split_amount: data?.charge?.amount,
            number_of_split: formik?.values?.number_of_split,
            amount_due: data?.Charges?.amount_due ?? data?.charge?.amount_due,
            note:
              data?.comment_invoice?.note != undefined ||
                data?.comment_invoice?.note != ""
                ? data?.comment_invoice?.note
                : data?.allData?.[0]?.comment_invoice?.note != undefined ||
                  data?.allData?.[0]?.comment_invoice?.note != ""
                  ? data?.allData?.[0]?.comment_invoice?.note
                  : formik?.values?.allData?.[0]?.comment_invoice?.note,
            comment_checkbox: data?.comment_invoice?.visible
              ? data?.comment_invoice?.visible
              : data?.allData?.[0]?.comment_invoice?.visible
                ? data?.allData?.[0]?.comment_invoice?.visible
                : formik?.values?.allData?.[0]?.comment_invoice?.visible,
            // note:
            //   data?.comment_invoice?.note ??
            //   formik?.values?.allData?.[0]?.comment_invoice?.note,

            // comment_checkbox:
            //   data?.comment_invoice?.visible ??
            //   formik?.values?.allData?.[0]?.comment_invoice?.visible,
            donation_donorName: data?.charge_type === "donation" ? data?.tribute_info?.donorName || "" : undefined,
            applyTributeInfo: data?.tribute_info?.type ? true : false,
            tribute_type: data?.tribute_info?.type ? {
              name: data?.tribute_info?.type
            } : undefined,
            donor_name: data?.tribute_info?.name ? data?.tribute_info?.name : undefined,
            message: data?.tribute_info?.message ? data?.tribute_info?.message : undefined,
            showAcknowledgement: data?.tribute_info?.acknowledgment ? true : false,
            acknowledgements: data?.tribute_info?.recipients?.length ? data?.tribute_info?.recipients?.map((item) => {
              return {
                email: item.email,
                recipient_name: item.recipient_name
              }
            }
            )
              : undefined,
          },
        ],
        paymentDate: new Date(),
        accountInfo: account ?? {},
      }
      : {
        payments: [],
        paymentMethods: [],
        nextPayment: false,

        values: [
          {
            tax_type: "%",
            discount_type: "%",
            payment_date: moment(new Date())?.format("YYYY-MM-DD"),
            is_splitted: false,
            isRecurring: false,
            recurringStart: new Date(),
            recurringEnd: null,
            SubLedger: "",
            SubLedgerName: "",
            charge_type: "charge",
          },
        ],
        checks: [],
        paymentDate: new Date(),
        allowCreditInstallment: false,
        installmentCreditDetails: {
          cycle: "",
          frequency: "",
          startInstallment: new Date(),
        },
        allowAchInstallment: false,
        installmentAchDetails: {
          cycle: "",
          frequency: "",
          startInstallment: new Date(),
        },
        accountInfo: account ?? {},
      };
  /**
   *
   * @param {Record<any, any>} values
   * @param {() => void} handleNext
   */

  useEffect(() => {
    if (data) {
      if (data?._id) {
        setTransactionIds([data._id]);
        setTransactionId(data?._id);
      }
      setChargeId(data?.charge?._id);
    }
  }, [data]);

  useEffect(() => {
    getCreditOnAccountAmount(
      formik?.values?.all_data?.Account?.id ?? formik?.values?.accountIds
    );
  }, []);

  const getCreditOnAccountAmount = async (id) => {
    if (id) {
      const token = await getAccessTokenSilently();
      await FinanceService.getCreditOnAccountAmount(id, token)
        .then((res) => {
          setCreditOnAccountAmount(res?.result?.balance);
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message);
        });
    }
  };

  const displayValidation = (msg) => {
    toastr.info("Validation", msg);
  }

  const getValueByPath = (obj, path) => {
    return path.split('.').reduce((acc, curr) => acc && acc[curr], obj);
  };
  const checkTributeValidation = (values) => {

    const validationRules = [
      { field: 'tribute_type.name', message: 'Please select tribute type' },
      { field: 'donor_name', message: 'Please enter donor name' },
    ];
    let valid = true;

    const tributeValidation = values?.values?.forEach(item => {
      if (!item?.donation_donorName) {
        displayValidation('Please enter donor name');
        valid = false;
        return true;
      }
      if (!item?.applyTributeInfo) return false;
      validationRules.some(rule => {
        const value = getValueByPath(item, rule.field);
        if (!value || (typeof value === 'string' && value.trim() === '')) {

          displayValidation(rule.message);

          valid = false;
          return true;
        }

        if (rule.validationFunction && !rule.validationFunction(value)) {

          displayValidation(rule.message);
          valid = false;
          return true;
        }
      }
      );
    });

    return valid;
  };

  const checkTributeAcknowledgementValidation = (values) => {
    const validationRules = [
      { field: 'recipient_name', message: 'Please enter recipient name' },
      { field: 'email', message: 'Please enter valid email', validationFunction: emailValidation },
    ];

    let valid = true;

    const tributeValidation = values?.values?.forEach(item => {
      if (!item?.applyTributeInfo || !item?.showAcknowledgement) return false;
      const acknowledgementsCheck = item?.acknowledgements?.some(ack => {
        validationRules.some(rule => {
          const value = getValueByPath(ack, rule.field);

          if (!value || (typeof value === 'string' && value.trim() === '')) {
            displayValidation(rule.message);
            valid = false;
            return true;
          }
          if (rule.validationFunction && !rule.validationFunction(value)) {
            displayValidation(rule.message);
            valid = false;
            return true;
          }
          return false;
        });


      });
      return !valid;
    });
    return valid;
  };

  const checkTransactionNotes = (values) => {
    let valid = true;
    const validNotes = values?.values?.forEach(item => {
      if (item?.require_notes && !item?.note?.trim()) {
        displayValidation('Please fill the required note field');
        valid = false;
      }
    });
    return valid;
  };


  const cantEditInstallmentHandler = useCallback((values, item) => {
    if (item?.ChargeStatus === "close" && (+values?.actual_total - (+values?.totalPaid || 0)) <= 0
      || item?.ChargeStatus === "close" && (+values?.actual_total - +values?.current_amount) <= 0) {
      return true;
    }
    return false;
  }, []);

  const validInstallment = (values) => {
    let valid = true;
    const validInstallments = values?.values?.forEach(item => {
      const cantEditInstallment = cantEditInstallmentHandler(values, item);
      if (item?.isInstallmentTransaction && !item?.installmentDetailsTransaction?.numberOfInstallment && !cantEditInstallment) {
        displayValidation('Please select number of installments');
        valid = false;
      }
    });
    return valid;
  };

  const validateStepOneRequiredFields = (values) => {
    const { account_name, values: transactions } = values;

    if (!account_name?.id) {
      toastr.info("Validation", "Account name is required");
      return false;
    }

    if (!transactions?.every((item) => !!item?.Ledger?.id)) {
      toastr.info("Validation", "Ledger account is required");
      return false;
    }

    if (!transactions?.every((item) => !!item?.total_amount)) {
      toastr.info("Validation", "Amount is required");
      return false;
    }

    if (
      !transactions?.every(
        (item) =>
          item?.charge_type === "credit_on_account" ||
          item?.charge_type === "credit on account" ||
          !!item?.due_date
      )
    ) {
      toastr.info("Validation", "Due date is required");
      return false;
    }

    return true;
  };

  const onStepClick = (
    values,
    handleNext,
    setFieldValue,
    currentStep,
    dontCall = false
  ) => {
    const { account_name, values: transactions, checks } = values;
    const enableHandleNext = transactions?.every((item) => {
      const { Ledger, due_date, payment_date, total_amount, charge_type } =
        item;
      return !!(
        Ledger &&
        (charge_type === "credit_on_account" ||
          charge_type === "credit on account" ||
          due_date) &&
        payment_date &&
        total_amount
      );
    });

    if (
      values?.values?.[0]?.charge_type !== "credit_on_account" &&
      !!values?.values?.[0]?.isRecurring &&
      values?.values?.[0]?.recurring_duration === ""
      // && values.type !== "edit-charge"
    ) {
      return toastr.info("Validation", "Please select recurring duration");
    }

    if (
      values?.values?.[0]?.charge_type !== "credit_on_account" &&
      !!values?.values?.[0]?.isRecurring &&
      !values?.values?.[0]?.cycle
    ) {
      return toastr.info("Validation", "Please select billing cycle");
    }

    if (
      values?.values?.[0]?.charge_type !== "credit_on_account" &&
      !!values?.values?.[0]?.isRecurring &&
      !values?.values?.[0]?.recurringStart
    ) {
      return toastr.info("Validation", "Please select start/end date");
    }

    if (currentStep === 0 && !validateStepOneRequiredFields(values)) {
      return;
    }

    if (
      currentStep === 1 &&
      checks?.length > 0 &&
      values?._selectedPaymentMethodTitles?.includes("check")
    ) {
      const checkValidate = checks.every((item) => {
        const { amount, check_date, check_number } = item;
        return !!(amount && check_date && check_number);
      });
      if (!checkValidate) {
        return toastr.info(
          "Validation",
          "Please fill all the required fields in check payment method"
        );
      }
    }

    // if (values?.require_notes === true) {
    //   if (
    //     values?.type_of_charge === "charge" &&
    //     !values?.transactions?.[0]?.note?.match(/^(?![\s-])[\w\s-]+$/) &&
    //     !values?.values?.[0]?.note?.match(/^(?![\s-])[\w\s-]+$/)
    //   ) {
    //     return toastr.info("Validation", "Please fill the required note field");
    //   }
    // }

    // const checkComma = (str) => {
    //   return str?.includes(',');
    // };
    function checkComma(number) {
      return /\d+,\d+/.test(number?.toString());
    }

    const hasComma = currentStep === 1 ? checkComma(values?.onAccountCredit_payment) : false;
    const hasCommas = currentStep === 1 ? checkComma(values?.creditOnAccountAmount) : false;


    const creditValue = hasComma ? values?.onAccountCredit_payment?.replace(/,/g, '') : values?.onAccountCredit_payment;
    const creditAmount = hasCommas ? values?.creditOnAccountAmount?.replace(/,/g, '') : values?.creditOnAccountAmount;

    const floatValue = hasComma ? parseFloat(creditValue) : creditValue;
    const floatAmount = hasCommas ? parseFloat(creditAmount) : creditAmount;


    if (currentStep === 0 && (values.values[0].charge_type === "donation" || values.values[0].charge_type === "charge")) {
      if (!checkTransactionNotes(values)) return;
    }

    if (currentStep === 0 && values.values[0].charge_type === "donation") {
      if (!checkTributeValidation(values)) return;
    }

    if (currentStep === 0 && values.values[0].charge_type === "donation") {
      if (!checkTributeAcknowledgementValidation(values)) return;
    }

    if (currentStep === 0 && !validInstallment(values)) return;


    if (currentStep === 1) {
      if (values?.cc_future_enabled && !values?.cc_future_date) {
        return toastr.info("Validation", "Future payment date is required for Credit Card");
      }
      if (values?.ach_future_enabled && !values?.ach_future_date) {
        return toastr.info("Validation", "Future payment date is required for ACH");
      }
      if (Number(floatValue) > Number(floatAmount)) {

        return toastr.info(
          "Validation",
          "Amount must be less than or equal to wallet balance"
        );
      }
    }
    // if (values?.require_notes === true) {
    //   if (
    //     (values?.type_of_charge == "credit_on_account" ||
    //       values?.type_of_charge == "credit on account") &&
    //     !values?.values?.[0]?.note?.match(/^(?![\s-])[\w\s-]+$/)
    //   ) {
    //     return toastr.info("Validation", "Please fill the required note field");
    //   }
    // }
    if (values?.values?.[0]?.total_amount === 0) {
      return toastr.info("Validation", "Amount will not be 0");
    }
    if (
      values?.values?.[0]?.discount_type === "%" &&
      parseFloat(values?.values?.[0]?.discount) > 100
    ) {
      return toastr.info("Validation", "Discount cannot be more than 100%");
    }
    if (
      values?.values?.[0]?.tax_type === "%" &&
      parseFloat(values?.values?.[0]?.tax) > 100
    ) {
      return toastr.info("Validation", "Tax cannot be more than 100%");
    }
    if (currentStep === 0 &&
      values?.values?.[0]?.discount_type === "$" &&
      parseFloat(values?.values?.[0]?.discount) >
      parseFloat(values?.values?.[0]?.total_amount)
    ) {
      return toastr.info("Validation", "Discount must be smaller than amount");
    }

    if (
      values?.values?.[0]?.is_splitted &&
      (values?.values?.[0]?.number_of_split === "0" ||
        values?.values?.[0]?.number_of_split === "")
    ) {
      return toastr.info(
        "validation",
        "Number of split should be greater than zero!"
      );
    }
    if (
      !values?.selectedUpdatedPayData &&
      !values?.selectedUpdatePayDataMethod &&
      values.values?.[0]?.isRecurring &&
      currentStep === 1
    ) {
      return toastr.info("Validation", "Please select at least one card");
    }
    if (
      values?.selectedUpdatedPayData === undefined &&
      !!values?.selectedUpdatePayDataMethod &&
      values.values?.[0]?.isRecurring &&
      values.checkRecurring === undefined &&
      currentStep === 1
    ) {
      return toastr.info("Validation", "Please select at least one card");
    }

    if (
      values?.selectedUpdatedPayData === undefined &&
      !!values?.selectedUpdatePayDataMethod &&
      values.values?.[0]?.isRecurring &&
      currentStep === 1
    ) {
      return toastr.info("Validation", "Please select at least one card");
    }

    const methodsValues = Object.entries(values)
      .filter(([key]) => key.includes("_payment"))
      .map(([key, value]) => {
        const split = key.split("_payment");
        if (split[0] === "check" && !value.check_number) {
          return undefined;
        }
        if (split[0].includes("credit")) {
          return {
            ...value,
            paymentMethod: split[0],
            amount: isNaN(values?.creditCardAmount) ? "" : values?.creditCardAmount,
            last4: values?.last4 /* cvc: values.cvc */,
          };
        }
        if (split[0].includes("ach")) {
          return {
            ...value,
            paymentMethod: split[0].toUpperCase(),
            amount: isNaN(values?.ach) ? "" : values?.ach ?? "",
            achId: values?.payments[0]?.ACH /* cvc: values.cvc */,
          };
        }
        if (split[0].includes("cash")) {
          return {
            ...value,
            paymentMethod: split[0] === "cash" ? "Cash" : "",
            amount: isNaN(values?.cash_payment) ? "" : values?.cash_payment,
          };
        }
        if (split[0].includes("onAccountCredit")) {
          return {
            ...value,
            paymentMethod:
              split[0] === "onAccountCredit" ? "Credit On Account" : "",
            amount: isNaN(values?.onAccountCredit_payment) ? "" : values?.onAccountCredit_payment,
          };
        }
        return {
          paymentMethod: split[0],
          amount: isNaN(values?.[key]) ? "" : values?.[key],
        };
      })
      .filter((item) => item !== undefined);


    let allAmountsEmpty =
      methodsValues.every(obj => !(parseFloat(obj.amount) > 0)) &&
      !(parseFloat(values?.creditCardAmount) > 0) &&
      !(parseFloat(values?.ach) > 0) &&
      !(parseFloat(values?.cash_payment) > 0) &&
      !(parseFloat(values?.onAccountCredit_payment) > 0) &&
      !values?.checks?.some(c => parseFloat(c.amount) > 0);


    if (values?.allData) {
      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.creditCardAmount &&
          // !values?.cardDetail
          values?.cardDetail?.length === 0
        ) {
          return toastr.info(
            "Validation",
            "Please select at least one credit card"
          );
        }
      }
    }

    if (values?.allData) {
      if (
        currentStep === 1 &&
        allAmountsEmpty && values?.checks?.length === 0
      ) {
        return toastr.info(
          "Validation",
          "At least one payment method should contain an amount"
        );
      }

      return handleNext();
    }

    if (account_name && enableHandleNext) {
      if (
        !values?.isTransactionSave ||
        (values.type === "edit-charge" && dontCall)
      ) {
        saveTransaction(
          {
            ...values,
            transactions: values.values,
          },
          setFieldValue
        );
      }

      if (checks.length > 0 && values?._selectedPaymentMethodTitles?.includes("check")) {
        const checkValidate = checks.every((item) => {
          const { amount, check_date, check_number } = item;
          return !!(amount && check_date && check_number);
        });
        if (currentStep === 1 && !checkValidate) {
          return toastr.info(
            "Validation",
            "Please fill all the required fields in check payment method"
          );
        }
      }

      const methods = Object.entries(values)
        .filter(([key]) => key.includes("_payment"))
        .some(([, value]) => !!value);

      let givenVal = [];

      const checkMethods = Object.entries(values)
        .filter(([key]) => key.includes("_payment"))
        .some(([, value]) => givenVal.push(value));

      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.ach &&
          !values?.selectedACH?.id
        ) {
          return toastr.info(
            "Validation",
            "Please select at least one ACH"
          );
        }
      }

      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.creditCardAmount &&
          values?.cardDetail?.length === 0
        ) {
          return toastr.info(
            "Validation",
            "Please select at least one credit card"
          );
        }
      }

      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.creditCardAmount &&
          values?.allowCreditInstallment &&
          !values?.installmentCreditDetails?.cycle
        ) {
          return toastr.info(
            "Validation",
            "Installments is required"
          );
        }
      }

      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.creditCardAmount &&
          values?.allowCreditInstallment &&
          !values?.installmentCreditDetails?.startInstallment
        ) {
          return toastr.info(
            "Validation",
            "First Payment Date is required"
          );
        }
      }


      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.ach &&
          values?.allowAchInstallment &&
          !values?.installmentAchDetails?.cycle
        ) {
          return toastr.info(
            "Validation",
            "Installments is required"
          );
        }
      }

      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.ach &&
          values?.allowAchInstallment &&
          !values?.installmentAchDetails?.startInstallment
        ) {
          return toastr.info(
            "Validation",
            "First Payment Date is required"
          );
        }
      }


      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          allAmountsEmpty && values?.checks?.length === 0
        ) {
          return toastr.info(
            "Validation",
            "At least one payment method should contain an amount"
          );
        }
      }


      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.checks?.length === 0 &&
          !values?.creditCardAmount &&
          // !values?.ach &&
          !methods &&
          !values?.selectedACH
        ) {
          return toastr.info(
            "Validation",
            "Please select at least one payment method"
          );
        }
      }


      if (!values?.values?.[0]?.isRecurring) {
        if (
          currentStep === 1 &&
          values?.onAccountCredit_payment &&
          values?.creditOnAccountAmount === 0
        ) {
          return toastr.info(
            "Validation",
            "Please select an other payment method your balance is zero"
          );
        }
      }

      if (
        currentStep === 1 &&
        values?.is_third_party &&
        values?.third_party?.name === values?.account_name?.name
      ) {
        return toastr.info(
          "Validation",
          "3rd party account will not be same as account name"
        );
      }

      handleNext();
    } else {
      toastr.info("Validation", "Please fill all the required ");
    }
  };

  useEffect(() => {
    onStepClickvalues(inputValue, 1);
  });



  const saveTransaction = (values, setFieldValue, currentStepIndex) => {
    // const subledger = values?.values?.map((item) => {
    //   return item?.SubLedger;
    // });

    async function fetchMyAPI() {
      const data = values.transactions?.map((item) => {
        const cantEditInstallment = cantEditInstallmentHandler(values, item);

        const due_Date = item?.due_date;
        let baseObj = {
          Account: !!accountTransaction
            ? {
              id: accountTransaction?._id,
              name: accountTransaction?.family_name,
            }
            : values.account_name,
          Ledger: item.Ledger,
          SubLedger: item?.SubLedger?.id ? item?.SubLedger : {},
          charge_type:
            item.charge_type == "credit on account"
              ? "credit_on_account"
              : item.charge_type || "charge",
          total_amount: item.total_amount,
          date: item?.payment_date ? new Date(item.payment_date) : new Date(),
          is_splitted: false,
          isRecurring: false,
          discount: item?.discount === "" ? 0 : item?.discount,
          tax_deductible: item?.tax === "" ? 0 : item?.tax,
          tax_type: item.tax_type,
          discount_type: item.discount_type,
          account_receivable: values?.account_receivable,

          ...(item.charge_type === "donation" ? {
            tribute_info: {
              donorName: item?.donation_donorName ?? "",
              ...(item?.applyTributeInfo ? {
                type: item?.tribute_type?.name ?? "",
                name: item?.donor_name ?? "",
                message: item?.message ? item?.message : undefined,
                acknowledgment: item?.showAcknowledgement,
                recipients: item?.showAcknowledgement ? item?.acknowledgements : undefined,
              } : {})
            }
          } : {}),

          isInstallmentTransaction: cantEditInstallment ? undefined : item?.isInstallmentTransaction,
          ...((item?.isInstallmentTransaction && !cantEditInstallment) ? {
            installmentDetailsTransaction: {
              numberOfInstallment: item?.installmentDetailsTransaction?.numberOfInstallment,
              lastInstallmentDate: item?.installmentDetailsTransaction?.lastInstallmentDate || undefined,
            }
          } : {}),

        };


        if (item.charge_type !== "credit on account") {
          baseObj = {
            ...baseObj,
            due_date: due_Date,
          };
        }
        if (item.charge_type !== "credit on account") {
          baseObj = {
            ...baseObj,
            isRecurring: item?.isRecurring ?? false,
          };
        }

        if (item.charge_type !== "credit on account") {
          baseObj = {
            ...baseObj,
            is_splitted: item.is_splitted ?? false,
          };
        }

        // if (
        //   item.charge_type === "donation" &&
        //   values?.tribute_type?.name !== "" &&
        //   values?.assign_email !== "" &&
        //   values?.email !== ""
        // ) {
        //   baseObj = {
        //     ...baseObj,
        //     tribute_info: {
        //       type: values?.tribute_type?.name ?? "",
        //       recipient_name: values?.donation_recipient_name ?? "",
        //       donor_name: values?.donor_name ?? "",
        //       assign_tribute: values?.assign_email ?? "",
        //       receipt: {
        //         id: values?.tribute_information?.id ?? "",
        //         name: values?.tribute_information?.name ?? "",
        //       },
        //       message: values?.note ?? "",
        //       recipient_salutation:
        //         values?.donation_recipient_salutations?.name ?? "",
        //       donor_salutation: values?.donor_salutation?.name ?? "",
        //       send_letter_to: {
        //         email: values?.email ?? "",
        //         address: values?.address ?? "",
        //       },
        //     },
        //   };
        // }

        // if (subledger?.id) {
        //   baseObj = {
        //     ...baseObj,
        //     SubLedger: values.transactions?.map((item) => {
        //       return item?.SubLedger;
        //     })[0],
        //   };
        // }

        if (item?.is_splitted && item.charge_type !== "credit on account") {
          baseObj = {
            ...baseObj,
            number_of_split: item?.number_of_split?.toString() ?? "0",
            split_duration: item.split_duration ?? "month",
          };
        }

        if (
          item.charge_type !== "credit on account" &&
          item?.isRecurring &&
          values.type !== "edit-charge"
        ) {
          baseObj = {
            ...baseObj,
            frequency: item?.recurring_duration?.id,
            cycle: item?.cycle?.value,
            cycleType: item?.cycle?.value === "forever" ? "forever" : "number",
            recurringStart: item?.recurringStart,
            recurringEnd: item?.recurringEnd,
          };
        }

        if (
          values?.note || values?.values?.[0]?.note
        )
          // if (
          //   (item.charge_type == "credit on account" ||
          //     item.charge_type == "credit_on_account") &&
          //   values?.values?.[0]?.note
          // ) {
          //   baseObj = {
          //     ...baseObj,
          //     comment_invoice: {
          //       visible: "false",
          //       note: values?.values?.[0]?.note,
          //     },
          //   };
          // }
          baseObj = {
            ...baseObj,
            comment_invoice: {
              visible: item?.comment_checkbox
                ? item?.comment_checkbox
                : false,
              note: item?.note
                ? item?.note
                : values?.values?.[0]?.note
                  ? values?.values?.[0]?.note
                  : values?.note,
            },
          };
        // if (item.charge_type === "charge") {
        //   baseObj = {
        //     ...baseObj,
        //     comment_invoice: {
        //       visible: values?.comment_checkbox ?? "false",
        //       note: values?.note ?? " ",
        //     },
        //   };
        // }
        if (
          item.due_date &&
          (item.charge_type === "charge" ||
            (item.charge_type === "donation" && values.type !== "edit-charge"))
        ) {
          return (baseObj = {
            ...baseObj,
            due_date: due_Date,
          });
        }
        // if (
        //   item.charge_type === "donation" &&
        //   values?.tribute_type?.name !== "" &&
        //   values.type !== "edit-charge"
        // ) {
        //   baseObj = {
        //     ...baseObj,
        //     tribute_info: {
        //       type: values?.tribute_type?.name ?? " ",
        //     },
        //   };
        // }

        // if (
        //   item.charge_type === "donation" &&
        //   values?.donation_recipient_name !== "" &&
        //   values?.type !== "edit-charge"
        // ) {
        //   baseObj = {
        //     ...baseObj,
        //     tribute_info: {
        //       recipient_name: values?.donation_recipient_name ?? " ",
        //     },
        //   };
        // }

        // if (
        //   item.charge_type === "donation" &&
        //   values?.donor_name !== "" &&
        //   values.type !== "edit-charge"
        // ) {
        //   baseObj = {
        //     ...baseObj,
        //     tribute_info: {
        //       donor_name: values?.donor_name ?? " ",
        //     },
        //   };
        // }

        // if (
        //   item.charge_type === "donation" &&
        //   values?.assign_email !== "" &&
        //   values?.email !== "" &&
        //   values.type !== "edit-charge"
        // ) {
        //   baseObj = {
        //     ...baseObj,
        //     tribute_info: {
        //       assign_tribute: values?.assign_email,
        //       receipt: {
        //         id: values?.tribute_information?.id,
        //         name: values?.tribute_information?.name,
        //       },
        //       message: values?.note,
        //       recipient_salutation:
        //         values?.donation_recipient_salutations?.name,
        //       donor_salutation: values?.donor_salutation?.name,
        //       send_letter_to: {
        //         email: values?.email,
        //         address: values?.address,
        //       },
        //     },
        //   };
        // }

        if (
          item.charge_type === "credit_on_account" ||
          item.charge_type === "donation"
        ) {
          baseObj = {
            ...baseObj,
          };
        }
        // if (values) {
        //   baseObj = {
        //     ...baseObj,
        //     SubLedger: values?.values?.map((item) => {
        //       return item?.SubLedger;
        //     })[0],
        //   };
        // }

        return baseObj;
      });

      const token = await getAccessTokenSilently();


      if (
        values?.values?.[0]?.discount_type === "%" &&
        parseFloat(values?.values?.[0]?.discount) > 100
      ) {
        return toastr.info("Validation", "Discount cannot be more than 100%");
      }
      if (
        values?.values?.[0]?.tax_type === "%" &&
        parseFloat(values?.values?.[0]?.tax) > 100
      ) {
        return toastr.info("Validation", "Tax cannot be more than 100%");
      }
      if (
        values?.values?.[0]?.discount_type === "$" &&
        parseFloat(values?.values?.[0]?.discount) >
        parseFloat(values?.values?.[0]?.total_amount)
      ) {
        return toastr.info(
          "Validation",
          "Discount must be smaller than amount"
        );
      }

      if (!transactionIds.length) {
        setDisabled(false);

        if (data?.[0]?.charge_type === "credit_on_account") {
          delete data?.[0]?.due_Date;
        }

        if (data?.[0]?.charge_type === "credit_on_account") {
          delete data?.[0]?.discount;
          delete data?.[0]?.discount_type;
        }

        if (
          data?.[0]?.is_splitted &&
          (data?.[0]?.number_of_split === "0" ||
            data?.[0]?.number_of_split === "")
        ) {
          return toastr.info(
            "validation",
            "Number of split should be greater than zero!"
          );
        }

        if (data?.[0]?.require_notes === true && data?.[0]?.note === "") {
          return toastr.info("Please add some notes");
        }

        setLoading(true);

        await FinanceService.createTransaction({ transactions: data }, token)
          .then((res) => {
            setLoading(false);
            setTransactionIds(res.result?.map((item) => item._id));
            setChargeIds(res.result?.map((item) => item.Charges?.[0]?._id));
            setChargeId(
              res.result?.[0]?.Charges?.[0]?._id ?? res.result?.[0]?._id
            );
            setTransactionId(res.result?.[0]?._id);
            setResTransId(res.result?.[0]?._id);
            window.dispatchEvent(
              new CustomEvent("save-transaction", {
                detail: res?.result,
              })
            );
            if (currentStepIndex === 0) {
              handleClose();
            }
            setInputValues(values);

            toastr.success("Success", "Transaction Saved Successfully");
            setFieldValue("isTransactionSave", true);
          })
          .catch((err) => {
            toastr.error("Error", err?.data?.message);
            setLoading(false);
          });
      }
      if (transactionIds.length) {
        const payload = { ...data[0], transactionId: transactionIds[0] };
        delete payload.isRecurring;

        if (payload.charge_type === "credit_on_account") {
          delete payload.discount;
        }
        if (
          payload.charge_type === "credit_on_account" &&
          payload.due_date === null
        ) {
          delete payload.due_date;
        }

        if (
          payload?.is_splitted &&
          (payload?.number_of_split === "0" || payload?.number_of_split === "")
        ) {
          return toastr.info(
            "validation",
            "Number of split should be greater than zero!"
          );
        }

        setLoading(true);
        await FinanceService.updateTransaction(
          {
            // transactions:
            // data.map((item, idx) => ({
            // ...item,
            // {
            payload,
            // }
            // })),
          },
          token
        )
          .then((res) => {
            setLoading(false);
            window.dispatchEvent(
              new CustomEvent("save-transaction", {
                detail: res?.result,
              })
            );
            setChargeId(res.result?.Charges?.[0]?._id);
            setResTransId(res?.result?._id);
            if (currentStepIndex === 0) {
              handleClose();
            }
            setInputValues(values);

            toastr.success("Success", "Transaction Updated Successfully");
            if (res?.futurePaymentDisabled) {
              toastr.info("Future payment", "Future payment has been disabled.");
            }
          })
          .catch((err) => {
            setLoading(false);
            toastr.error("Error", err?.data?.message);
          });
      }
    }
    const { account_name, values: transactions } = values;
    const enableHandleNext = transactions.every((item) => {
      const { Ledger, due_date, payment_date, total_amount, charge_type } =
        item;
      return !!(
        charge_type === "credit on account" ||
        charge_type === "credit_on_account" ||
        due_date ||
        (Ledger && payment_date && total_amount)
      );
    });

    if (!validInstallment(values)) return;

    if (account_name && enableHandleNext) {

      if (values.values[0].charge_type === "donation" || values.values[0].charge_type === "charge") {
        if (!checkTransactionNotes(values)) return;
      }

      if (values.values[0].charge_type === "donation") {
        if (!checkTributeValidation(values)) return;
      }

      if (values.values[0].charge_type === "donation") {
        if (!checkTributeAcknowledgementValidation(values)) return;
      }
      fetchMyAPI();
    } else if (values?.values?.[0]?.total_amount === 0) {
      return toastr.info("Validation", "Amount will not be 0");
    } else {
      toastr.info("Validation", "Please fill all the required fields");
    }
  };

  const delay = (ms) =>
    new Promise((resolve) => setTimeout(() => resolve(), ms));

  // a recurring charge is fully scheduled when it has an end date, or when its
  // billing period is "Forever" (which never has an end date)
  const hasRecurringEnd = (values) =>
    !!values?.values?.[0]?.recurringEnd ||
    values?.values?.[0]?.cycle?.value === "forever" ||
    values?.values?.[0]?.cycle === "forever";

  const onRecurringPayment = async (values, currentStepIndex) => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    const paymentMethods = Object.entries(values)
      .filter(([key]) => key.includes("_payment"))
      .map(([key, value]) => {
        const split = key.split("_payment");
        if (
          split[0].includes("credit") &&
          parseFloat(values.creditCardAmount)
        ) {
          return {
            ...value,
            paymentMethod:
              split[0] === "credit card" ? "credit_card" : split[0],
            amount: values?.creditCardAmount,
            /* cvc: values.cvc */
          };
        }
        if (split[0].includes("ach") && parseFloat(values.ach)) {
          return {
            paymentMethod: "ACH",
            amount: values?.ach,
            achId: values?.payments[0]?.ACH,
          };
        }
        if (split[0].includes("cash") && parseFloat(values.cash_payment) > 0) {
          return {
            paymentMethod: split[0] === "cash" ? "cash" : split[0],
            amount: values.cash_payment,
          };
        }
        if (
          split[0].includes("onAccountCredit") &&
          parseFloat(values?.onAccountCredit_payment)
        ) {
          return {
            paymentMethod:
              split[0] === "onAccountCredit" ? "credit_on_account" : split[0],
            amount: values?.onAccountCredit_payment,
          };
        }
        if (parseFloat(value) > 0) {
          return { amount: value, paymentMethod: split[0] };
        }
        return;
      })
      .filter((item) => item !== undefined);



    const recurringPayment = values?.allPaymentsMethods?.find((item) => (item?.type === values?.selectedUpdatePayDataMethod) || (item?.type === "credit card" && values?.selectedUpdatePayDataMethod === "credit_card"));
    const cardsdetail = [...paymentMethods, ...values?.checks];

    let payload = {
      account: values?.account_name?.id,
      recurringDetails: {
        providerId: values?.selectedUpdatedPayData ?? cardsdetail?.[0]?.cardId,
        transactionId: TransactionId,
        paymentMethodId: recurringPayment?._id,
      },
    };

    await FinanceService.RecurringPayment(payload, token)
      .then((res) => {
        if (res) {
          savePaymentHistory(values);
          toastr.success("Success", "Recurring Create Successfully");
          setBatchList(true);
          setLoading(false);
        }
        if (currentStepIndex === 2) {
          handleClose();
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message);
        setLoading(false);
      });
  };


  const getTotalPayment = (values) => {
    const totalAllDataDueAmount =
      formik?.values?.allData?.length > 0 &&
      formik?.values?.allData?.reduce(
        (accumulator, obj) => accumulator + obj.Charges?.amount_due,
        0
      );

    const totalSplittedAmount =
      formik?.values?.allData &&
      formik?.values?.allData?.map((item) => item?.charge?.amount);
    const sumOfSplittedAmount = totalSplittedAmount?.reduce(
      (partialSum, a) => partialSum + a,
      0
    );

    const totalAmount =
      formik.values.allData?.length > 0
        ? thousands((+totalAllDataDueAmount).toFixed(2))
        : values?.values?.[0]?.charge?.paid === 0
          ? thousands((+values?.actual_total).toFixed(2))
          : values?.values?.[0]?.charge?.paid
            ? thousands(
              (+values?.values?.[0]?.is_splitted === false
                ? values?.actual_total
                : Number(values?.values?.[0]?.total_amount) -
                values?.values?.[0]?.charge?.paid
              ).toFixed(2)
            )
            : values?.type === "edit-charge"
              ? thousands((+values?.actual_total).toFixed(2))
              : values?.amount_due === 0 || values?.amount_due
                ? thousands((+values?.amount_due).toFixed(2))
                : formik?.values?.allData?.[0]?.charge?.amount
                  ? thousands((+sumOfSplittedAmount).toFixed(2))
                  : values?.values?.[0]?.split_amount
                    ? thousands((+values?.values?.[0]?.split_amount).toFixed(2))
                    : values?.actual_total
                      ? thousands((+values?.actual_total).toFixed(2))
                      : values?.current_total
                        ? thousands((+values?.current_total).toFixed(2))
                        : values?.current_amount
                          ? thousands((+values?.current_amount).toFixed(2))
                          : values?.values?.[0]?.split_amount
                            ? thousands((+values?.values?.[0]?.split_amount).toFixed(2))
                            : thousands((+values?.values?.[0]?.total_amount).toFixed(2)) ??
                            thousands((+sumOfAmounts).toFixed(2));

    return totalAmount;
  };



  const paymentCalculation = (paymentMethods, values) => {
    const payments = paymentMethods?.map((item) => {
      const paymentMethod = values?.allPaymentsMethods?.find(
        (method) => (method?.type?.toLowerCase() === item?.paymentMethod?.toLowerCase() || (method?.type === "credit on account" && item?.paymentMethod === "credit_on_account") || (
          method?.type === "credit card" && item?.paymentMethod === "credit_card"
        ))
      );
      if (item?.amount > 0 && paymentMethod?.coverConvenienceFee) {
        return {
          ...item,
          paymentMethodId: paymentMethod?._id,
         coverFee:values[`${paymentMethod?._id}`],
          convenienceFee: paymentMethod?.convenienceFeeInfo?.amount,
        };
      } else {
        return {
          ...item,
          
          paymentMethodId: paymentMethod?._id,
          coverFee:values[`${paymentMethod?._id}`],
        };
      }
    });

    return payments?.length ? payments : paymentMethods;
  };

  const calculateConvenienceFeeAmount = (values) => {
    let paymentMethods = [];
    if (values?.values?.[0]?.isRecurring) {
      paymentMethods = calculationIsRecurringPaymentsMethods(values);
    } else {
      paymentMethods = calculationPaymentsMethods(values);
    }
    let amount = 0;
    const convenienceFee = paymentMethods?.map((item) => {
      if (item?.amount && item?.amount > 0) {
        const paymentMethod = values?.allPaymentsMethods?.find(
          (method) => (method?.type?.toLowerCase() === item?.paymentMethod?.toLowerCase() || (method.type === "credit on account" && item?.paymentMethod === "credit_on_account") || (
            method?.type === "credit card" && item?.paymentMethod === "credit_card"
          ))
        );
        
  if (!paymentMethod?.coverConvenienceFee) return;

         const optionalKey = `${paymentMethod?._id}`;

         if (values?.[optionalKey] === false) return;
        // if (paymentMethod?.coverConvenienceFee) {
          amount += paymentMethod?.convenienceFeeInfo?.type === "%" ? (item?.amount * paymentMethod?.convenienceFeeInfo?.amount) / 100 : paymentMethod?.convenienceFeeInfo?.amount;
        // }
      }
    })


    const checksHasAmount = values?.checks?.some((item) => parseFloat(item.amount) > 0)
    if (checksHasAmount) {
      const paymentMethod = values?.allPaymentsMethods.find(
        (method) => method.type === "check"
      );
      const amounts = values?.checks?.reduce(
        (total, item) => total + parseFloat(item.amount || 0),
        0
      );
      // if (paymentMethod?.coverConvenienceFee) {
         if (!paymentMethod?.coverConvenienceFee) return amount;

         const optionalKey = `${paymentMethod?._id}`;

         if (values?.[optionalKey] === false) return amount;
        amount += paymentMethod?.convenienceFeeInfo?.type === "%" ? (amounts * paymentMethod?.convenienceFeeInfo?.amount) / 100 : paymentMethod?.convenienceFeeInfo?.amount;
      // }
    }
    return amount;
  }

  const calculationIsRecurringPaymentsMethods = (values) => {
    let filterPaymentMethods = [];
    const paymentsMethods = values?.allPaymentsMethods?.map((item) => {
      if ((values?.selectedUpdatePayDataMethod === item?.type || (values?.selectedUpdatePayDataMethod === "credit_card" && (item?.type === "credit_card" || item?.type === "credit card")))) {
        filterPaymentMethods.push({
          ...item,
          amount: isNaN(values?.actual_total) ? 0 : values?.actual_total,
          paymentMethod: (item?.type === "credit card" || item?.type === "credit_card") ? "credit_card" : item?.type,
        })
      }
    });
    return filterPaymentMethods;
  }

  const calculationPaymentsMethods = (values) => {
    const paymentMethods = Object.entries(values)
      .filter(([key]) => key.includes("_payment"))
      .map(([key, value]) => {
        const split = key.split("_payment");
        if (
          split[0].includes("credit") &&
          parseFloat(values.creditCardAmount)
        ) {
          return {
            ...value,
            paymentMethod:
              split[0] === "credit card" ? "credit_card" : split[0],
            amount: values?.creditCardAmount,
            cardId: values?.cardDetail?.id ?? values?.SelectedProviderId,
            /* cvc: values.cvc */
          };
        }
        if (split[0].includes("ach") && parseFloat(values.ach)) {
          return {
            paymentMethod: "ACH",
            amount: values?.ach,
            achId: values?.payments[0]?.ACH,
          };
        }
        if (split[0].includes("cash") && parseFloat(values.cash_payment) > 0) {
          return {
            paymentMethod: split[0] === "cash" ? "cash" : split[0],
            amount: values.cash_payment,
          };
        }
        if (
          split[0].includes("onAccountCredit") &&
          parseFloat(values?.onAccountCredit_payment)
        ) {
          return {
            paymentMethod:
              split[0] === "onAccountCredit" ? "credit_on_account" : split[0],
            amount: values?.onAccountCredit_payment,
          };
        }
        if (parseFloat(value) > 0) {
          return { amount: value, paymentMethod: split[0] };
        }
        return;
      })
      .filter((item) => item !== undefined);
    return paymentMethods;
  }

  const disabledPaymentDateHandler = (values) => {
    if (values?.values?.[0]?.isRecurring) {
      return values?.selectedUpdatePayDataMethod === "credit_card"
    }
    return values?.["credit card_payment"] && (values?.cardDetail?.id ?? values?.SelectedProviderId) && values?.creditCardAmount;
  }

  const onCheckout = async (values, currentStepIndex) => {
    if (checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    if (setBatchList !== undefined) {
      setBatchList(false);
    }
    setLoading(true);
    try {
    await delay(2000);
    if (!values?.isTransactionSave) {
      toastr.info("Validation", "Please save the transaction first");
      return;
    }
    const paymentMethods = Object.entries(values)
      .filter(([key]) => key.includes("_payment"))
      .map(([key, value]) => {
        const split = key.split("_payment");
        if (
          split[0].includes("credit") &&
          parseFloat(values.creditCardAmount)
        ) {
          return {
            ...value,
            paymentMethod:
              split[0] === "credit card" ? "credit_card" : split[0],
            amount: values?.creditCardAmount,
            cardId: values?.cardDetail?.id ?? values?.SelectedProviderId,
            /* cvc: values.cvc */
          };
        }
        if (split[0].includes("ach") && parseFloat(values.ach)) {
          return {
            paymentMethod: "ACH",
            amount: values?.ach,
            achId: values?.selectedACH?.id,
          };
        }
        if (split[0].includes("cash") && parseFloat(values.cash_payment) > 0) {
          return {
            paymentMethod: split[0] === "cash" ? "cash" : split[0],
            amount: values.cash_payment,
          };
        }
        if (
          split[0].includes("onAccountCredit") &&
          parseFloat(values?.onAccountCredit_payment)
        ) {
          return {
            paymentMethod:
              split[0] === "onAccountCredit" ? "credit_on_account" : split[0],
            amount: values?.onAccountCredit_payment,
          };
        }
        if (parseFloat(value) > 0) {
          return { amount: value, paymentMethod: split[0] };
        }
        return;
      })
      .filter((item) => item !== undefined);
    const token = await getAccessTokenSilently();
    const totalPaid = paymentMethods.reduce((a, b) => a + parseFloat(b.amount), 0);
    const totalAmount = getTotalPayment(values);

    if (values?.emailOnReceipt && !values?.emailsReceipt?.length) {
      return toastr.info("Validation", "Please add an email address");
    }

    if (!confirmPayment.current && data?.charge?.installmentDetails?.status === "active") {
      return openInstallmentModalHandler()
    }

    if (!confirmPayment.current && (totalPaid > totalAmount)) {
      setTotalPaidAmount(Math.abs(+totalPaid - +totalAmount).toFixed(2));
      return openPaymentModalHandler()
    }
    confirmPayment.current = false;
    const payments = paymentCalculation(paymentMethods, values);
    const checkPaymentMethod = values?.allPaymentsMethods.find(
      (method) => method?.type.toLowerCase() === "check"
    );
    const checks = values?.checks?.map((item) => {
      return {
        ...item,
        paymentMethodId: checkPaymentMethod?._id,
        coverFee:values[`${checkPaymentMethod?._id}`],
        ...(checkPaymentMethod?.coverConvenienceFee ? { convenienceFee: checkPaymentMethod?.convenienceFeeInfo?.amount } : {}),
      };
    });
    const disabledPaymentDate = disabledPaymentDateHandler(values);
    if (data?.allData || chargeIds?.length > 1) {
      await FinanceService.payAllSelectedCharges(
        {
          payments: [...payments, ...checks],
          // convenient_fee: 0,
          // convenient_fee_by: "admin",
          charges: data?.allData ? values?.allData?.map((item) => {
            return item?.charge?._id;
          }) : chargeIds,
          is_third_party: values?.is_third_party,
          third_party_id: values?.third_party?.id,
          account: values?.accountIds ? values?.accountIds : values?.account_name?.id,
          comment_receipt: {
            visible: values?.comment_receipt ?? "false",
            note: values.receipt_note ?? "test",
          },
          paymentDate: disabledPaymentDate ? undefined : values?.paymentDate,
          emailOnReceipt: values?.emailOnReceipt,
          emailsReceipt: values?.emailOnReceipt ? values?.emailsReceipt?.map((item) => item.email) : undefined,
        },
        token
      )
        .then((res) => {
          if (res) {
            window.dispatchEvent(new CustomEvent("save-transaction"));
            savePaymentHistory(values);
            toastr.success("Success", "Transaction successful");
            handleClose();
            if (setBatchList !== undefined) {
              setBatchList(true);
            }
          }
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message);
        });
      await delay(2000);
    } else {
      if (setBatchList !== undefined) {
        setBatchList(false);
      }

      const cardsdetail = [...paymentMethods, ...values?.checks];

      let updatePaymentMethod = [];
      const recurringPayment = values?.allPaymentsMethods?.find((item) => (item?.type === values?.selectedUpdatePayDataMethod) || (item?.type === "credit card" && values?.selectedUpdatePayDataMethod === "credit_card"));

      if (values?.values?.[0]?.isRecurring) {
        updatePaymentMethod = [
          {
            paymentMethodId: recurringPayment?._id,
            paymentMethod: values?.selectedUpdatePayDataMethod.toString(),
            amount: values?.actual_total.toString(),
            cardId: values?.cardDetail?.id ?? values?.SelectedProviderId,
            coverFee: values?.[`${recurringPayment?._id}`],
          },
        ];
      }

      const total_amount = values?.type === "edit-charge" ? values?.actual_total : values?.current_total ?? values?.actual_total;
      const calculatedTotalAmount = total_amount + calculateConvenienceFeeAmount(values);

      let payload = {
        payments: values?.values?.[0]?.isRecurring
          ? updatePaymentMethod
          : [...payments, ...checks],
        // convenient_fee: +values?.convenient_fee,
        // convenient_fee_by: values?.convenient_fee_by,
        is_third_party: values?.is_third_party,
        third_party_id: values?.third_party?.id,
        chargeId,
        account: !!accountTransaction
          ? accountTransaction?._id
          : values.account_name?.id,
        total_amount: calculatedTotalAmount,
        // total_amount: values?.current_total ?? values?.actual_total,
        comment_receipt: {
          visible: values?.comment_receipt ?? "false",
          note: values.receipt_note ?? " ",
        },
        allowInstallment: values?.allowCreditInstallment || values?.allowAchInstallment,
        installmentDetails: values?.allowCreditInstallment ? {
          cycle: values?.installmentCreditDetails?.cycle ?? "",
          frequency: "month",
          startInstallment: values?.installmentCreditDetails?.startInstallment
        } : values?.allowAchInstallment ? {
          cycle: values?.installmentAchDetails?.cycle ?? "",
          frequency: "month",
          startInstallment: values?.installmentAchDetails?.startInstallment
        } : undefined,
        emailOnReceipt: values?.emailOnReceipt,
        emailsReceipt: values?.emailOnReceipt ? values?.emailsReceipt?.map((item) => item.email) : undefined,
      };
      if (values?.values?.[0]?.isRecurring) {
        payload = {
          ...payload,
          useRecurring: values?.values?.[0]?.isRecurring,
          recurringDetails: {
            providerId:
              values?.cardDetail?.id ?? values?.SelectedProviderId,
            transactionId: TransactionId,
            paymentMethodId: recurringPayment?._id,
          },
        };
      }

      if (values?.values?.checkSplitting || values?.values?.checkAchSplit) {
        payload = {
          ...payload,
          useSplit: values?.values?.[0]?.is_splitted,
          spiltDetails: {
            providerId: cardsdetail?.[0]?.cardId,
            transactionId: TransactionId,
          },
        };
      }
      const disabledPaymentDate = disabledPaymentDateHandler(values);

      const futurePaymentDate = values?.cc_future_date || values?.ach_future_date;
      await FinanceService.checkout({
        ...payload,
        paymentDate: disabledPaymentDate ? undefined : values?.paymentDate,
        isFuturePayment: futurePaymentDate ? true : undefined,
        futurePaymentDate: futurePaymentDate
          ? moment(futurePaymentDate).format("YYYY-MM-DD")
          : undefined,
      }, token)
        .then((res) => {
          if (res) {
            window.dispatchEvent(new CustomEvent("save-transaction"));
            savePaymentHistory(values);
            toastr.success("Success", "Transaction successful");
            if (setBatchList !== undefined) {
              setBatchList(true);
            }
          }
          if (currentStepIndex === 2) {
            handleClose();
          }
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message);
        });
      await delay(2000);
    }
    } finally {
      checkoutInFlight.current = false;
      setLoading(false);
    }
  };

  const onStepClickvalues = (values, currentStep) => {
    const methods = Object.entries(values)
      .filter(([key]) => key.includes("_payment"))
      .some(([, value]) => !!value);
    if (
      currentStep === 1 &&
      (values?.checks?.length > 0 || values?.creditCardAmount || methods)
    ) {
      setPaidAmount(true);
    }
    if (
      currentStep === 1 &&
      values?.checks?.length === 0 &&
      !values?.creditCardAmount &&
      !methods
    ) {
      setPaidAmount(false);
    }
  };
  const onClose = () => {
    handleClose();
  };

  useEffect(() => {
    if (data) {
      formik.setFieldValue("all_data", data);
    }
  }, [data]);

  const OnUpdateAndPay = async (values) => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    const payload = {
      transactionId: values?.transaction_id ?? "",
      chargeId: values.charge_id,
      providerId: values.selectedUpdatedPayData,
    };
    FinanceService.updatePayemntInfo(payload, token)
      .then((res) => {
        setLoading(false);
        // setSelected(null);
        setHFinish(true);
        toastr.success("Success", "Payment Updated Successfully");
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        toastr.error("Error", err?.data?.message);
      });
  };


  const openPaymentModalHandler = () => {
    confirmPayment.current = false;
    setOpenPaymentModal(true);
  }

  const openInstallmentModalHandler = () => {
    confirmPayment.current = false;
    setOpenConfirmNewPaymentModal(true);
  }

  return (
    <div
      className={
        title === "New Transaction"
          ? "new-charge modal-background"
          : "new-charge"
      }
      ref={newChargeRef}
      style={stepMinHeight !== undefined ? { minHeight: stepMinHeight } : undefined}
    >
      <div
        className={
          data?.charge?.payment?.status === "declined"
            ? "new-charge-header my-9"
            : "new-charge-header pt-5"
        }
      >
        <span className="new-charge-header-title">{title}</span>
        <span className="new-charge-header-close" onClick={onClose}>
          <CrossSvg />
        </span>
      </div>
      <FormikWizard
        initialValues={{
          ...initialValues,
          convenient_fee_by: "admin",
          convenient_fee: 0,
        }}
        onSubmit={(values) => {
          // setFinalValues(values);
        }}
        validationSchema={NewChargeSchema}
        validateOnNext
        activeStepIndex={activeIndex}
        steps={[
          {
            component: ChargeDetails,
          },
          {
            component: PaymentInformation,
          },
          {
            component: (props) => <Review calculateConvenienceFeeAmount={calculateConvenienceFeeAmount} {...props} />,
          },
        ]}
      >
        {(wizardBag) => {
          const {
            currentStepIndex,
            renderComponent,
            handlePrev,
            handleNext,
            isNextDisabled,
            isPrevDisabled,
            values,
            setFieldValue,
          } = wizardBag;
          if (currentStepIndex === 1 || currentStepIndex === 2) {
            // setStepper("white");
          }
          if (activeStepForHeight !== currentStepIndex) {
            setActiveStepForHeight(currentStepIndex);
          }
          let dontCall = false;

          const isAnySplit = values?.values.some(rect => rect.is_splitted === true);

          return (
            <div className="charge-content">
              {data?.charge?.payment?.status !== "declined" && (
                <div className="charge-content-header py-15 charge-content-header-sticky">
                  <div className="fom-steps">
                    <Stepper activeStep={currentStepIndex}>
                      <Step
                        completed={currentStepIndex > 0}
                        className="step pl-0"
                      >
                        <StepLabel className="step-label">
                          Transaction Details
                        </StepLabel>
                      </Step>
                      <Step completed={currentStepIndex > 1}>
                        <StepLabel>Payment information</StepLabel>
                      </Step>
                      <Step completed={hfinish}>
                        <StepLabel>Review</StepLabel>
                      </Step>
                    </Stepper>
                  </div>
                  <div
                    className="charge-header-footer-actions"
                    ref={(node) => {
                      footerPortalRef.current = node;
                      if (node && !footerPortalReady) {
                        setFooterPortalReady(true);
                      }
                    }}
                  />
                </div>
              )}

              <div className="">
                {data?.charge?.payment?.status === "declined"
                  ? setTitle("Update Payment Information")
                  : currentStepIndex !== 0
                    ? setTitle("New Payment")
                    : values.type === "edit-charge"
                      ? setTitle("Edit Transaction")
                      : setTitle("New Transaction")}
                <div className="form-content">
                  <Box
                    ref={stepContentRef}
                    my="2rem"
                    className={`w-100 mb-0${currentStepIndex === 0 ? " transaction-details-box-spacing" : currentStepIndex === 2 ? " review-box-spacing" : ""}`}
                  >
                    {renderComponent()}
                  </Box>

                  {/* Hidden clone of Review, kept mounted at its default
                      (collapsed) height so its real height is known before
                      the user ever navigates to it. See reviewMeasureRef
                      effect above. */}
                  <div aria-hidden="true" style={{ height: 0, overflow: "hidden" }}>
                    <Box
                      ref={reviewMeasureRef}
                      my="2rem"
                      className="w-100 mb-0 review-box-spacing"
                    >
                      <Review
                        calculateConvenienceFeeAmount={calculateConvenienceFeeAmount}
                        {...wizardBag}
                        setFieldValue={() => {}}
                      />
                    </Box>
                  </div>

                  {data?.charge?.payment?.status === "declined" &&
                    values.values?.[0]?.isRecurring && (
                      <div className="col-sm-12 mb-5 pb-5 pt-5 text-center">
                        <MyButton
                          disabled={loading}
                          content={"Update and Pay"}
                          style={{ width: 120 }}
                          onClick={() => OnUpdateAndPay(values)}
                        />
                      </div>
                    )}
                  {data?.charge?.payment?.status !== "declined" && (
                    <div className="form-content-bottom">
                      {currentStepIndex === 2 && <hr />}
                      {footerPortalReady && footerPortalRef.current && createPortal(
                      <div className="form-footer">
                        {currentStepIndex === 1 && (values?.type === "edit-charge" || values?.type_of_charge === "charge" || values?.type_of_charge === "credit_on_account") ? (
                          <MyButton
                            type="secondary"
                            content={
                              <span>
                                <span className="mr-2"> {"<"}</span>
                                <span>
                                  {"Edit Changes"}
                                </span>
                              </span>
                            }
                            onClick={() => {
                              if (currentStepIndex - 1 === 0) {
                                setFieldValue(`nextPayment`, true);
                                setStepper(0);
                              }
                              if (currentStepIndex - 1 === 1) {
                                setFieldValue(`checkRecurring`, false);
                                setFieldValue(`checkAch`, false);
                              }
                              setFieldValue(`type`, "edit-charge");
                              setHFinish(false);
                              handlePrev();
                            }}
                          // disabled={currentStepIndex === 1 && values.values?.[0]?.isRecurring ? true : false}
                          // disabled={
                          //   (data?.charge?.status === "payment" ||
                          //     getSplittedArray ||
                          //     data?.is_splitted) &&
                          //     currentStepIndex === 1
                          //     ? true
                          //     : isPrevDisabled
                          // }
                          />
                        ) : currentStepIndex === 2 ? (
                          <MyButton
                            type="secondary"
                            content={
                              <span>
                                <span className="mr-2"> {"<"}</span>
                                <span>
                                  {"Payment information"}
                                </span>
                              </span>
                            }
                            onClick={() => {
                              if (currentStepIndex - 1 === 0) {
                                setFieldValue(`nextPayment`, true);
                                setStepper(0);
                              }
                              if (currentStepIndex - 1 === 1) {
                                setFieldValue(`checkRecurring`, false);
                                setFieldValue(`checkAch`, false);
                              }
                              setHFinish(false);
                              handlePrev();
                            }}
                          />
                        )
                          : currentStepIndex === 1 ? (
                            <MyButton
                              type="secondary"
                              style={{ visibility: "hidden", pointerEvents: "none" }}
                              content={
                                <span>
                                  <span className="mr-2"> {"<"}</span>
                                  <span>
                                    {"Payment information"}
                                  </span>
                                </span>
                              }
                            />
                          ) : (
                            <br />
                          )}
                        <div />

                        <div className="d-flex align-items-center">
                          {!values.values?.[0]?.isRecurring &&
                            currentStepIndex === 0 && (
                              <MyButton
                                style={{ marginRight: 10, height: 40 }}
                                type="secondary"
                                content={
                                  <span>
                                    <span>
                                      {hfinish ? "Cancel " : "Save transaction"}
                                    </span>
                                  </span>
                                }
                                // disabled={loading}
                                // disable={loading}
                                onClick={() => {
                                  setBtnContent("");
                                  if (hfinish) {
                                    onClose();
                                    return;
                                  }
                                  if (
                                    currentStepIndex === 0 &&
                                    !validateStepOneRequiredFields(values)
                                  ) {
                                    return;
                                  }
                                  saveTransaction(
                                    {
                                      ...values,
                                      transactions: values.values,
                                    },
                                    setFieldValue,
                                    currentStepIndex
                                  );
                                }}
                                disabled={
                                  loading
                                  // loading ? true :
                                  //   Object.keys(data).length !== 0 ? false :
                                  //     loading ||
                                  //     (currentStepIndex !== 2
                                  //       ? currentStepIndex === 0 &&
                                  //         (values?.type_of_charge === "charge" ||
                                  //           values?.type_of_charge ===
                                  //           "credit_on_account") &&
                                  //         values?.require_notes === true
                                  //         ? values?.values
                                  //           .map((item) => {
                                  //             return item?.total_amount &&
                                  //               item?.Ledger &&
                                  //               item?.payment_date &&
                                  //               values?.account_name &&
                                  //               item?.Ledger?.account_receivable &&
                                  //               item?.note !== "" &&
                                  //               item?.note?.match(
                                  //                 /^(?![\s-])[\w\s-]+$/,
                                  //                 "Enter a valid name"
                                  //               )
                                  //               ? false
                                  //               : true;
                                  //           })
                                  //           .includes(true)
                                  //         : currentStepIndex === 0 &&
                                  //           (values?.type_of_charge === "charge" ||
                                  //             values?.type_of_charge ===
                                  //             "credit_on_account") &&
                                  //           values?.require_notes === true
                                  //           ? values?.values
                                  //             .map((item) => {
                                  //               return item?.total_amount > 0 &&
                                  //                 item?.Ledger &&
                                  //                 item?.payment_date &&
                                  //                 values?.account_name &&
                                  //                 item?.Ledger?.account_receivable &&
                                  //                 item?.note !== "" &&
                                  //                 item?.note?.match(
                                  //                   /^(?![\s-])[\w\s-]+$/,
                                  //                   "Enter a valid name"
                                  //                 )
                                  //                 ? false
                                  //                 : true;
                                  //             })
                                  //             .includes(true)
                                  //           : currentStepIndex === 0 &&
                                  //             (values?.type_of_charge === "charge" ||
                                  //               values?.type_of_charge ===
                                  //               "credit_on_account") &&
                                  //             values?.require_notes === false
                                  //             ? values?.values
                                  //               .map((item) => {
                                  //                 return item?.Ledger &&
                                  //                   item?.total_amount &&
                                  //                   item?.payment_date &&
                                  //                   values?.account_name
                                  //                   ? // &&
                                  //                   // item?.note !== "" &&
                                  //                   // item?.note?.match(
                                  //                   //   /^(?![\s-])[\w\s-]+$/,
                                  //                   //   "Enter a valid name"
                                  //                   // )
                                  //                   false
                                  //                   : true;
                                  //               })
                                  //               .includes(true)
                                  //             : currentStepIndex === 0 &&
                                  //               (values?.type_of_charge === "charge" ||
                                  //                 values?.type_of_charge ===
                                  //                 "credit_on_account") &&
                                  //               values.type === "pay-now"
                                  //               ? values?.values
                                  //                 .map((item) => {
                                  //                   return item?.Ledger &&
                                  //                     item?.total_amount > 0 &&
                                  //                     item?.payment_date &&
                                  //                     values?.account_name &&
                                  //                     item?.note !== "" &&
                                  //                     item?.note?.match(
                                  //                       /^(?![\s-])[\w\s-]+$/,
                                  //                       "Enter a valid name"
                                  //                     )
                                  //                     ? false
                                  //                     : true;
                                  //                 })
                                  //                 .includes(true)
                                  //               : values?.cash_payment
                                  //                 ? false
                                  //                 : values?.selectedACH
                                  //                   ? values?.ach
                                  //                     ? false
                                  //                     : true
                                  //                   : values?.checks.length !== 0
                                  //                     ? values?.checks
                                  //                       .map((item) => {
                                  //                         return item?.Ledger &&
                                  //                           item?.total_amount &&
                                  //                           item?.payment_date &&
                                  //                           values?.account_name
                                  //                           ? false
                                  //                           : true;
                                  //                       })
                                  //                       .includes(true)
                                  //                     : currentStepIndex === 0 &&
                                  //                       values?.type_of_charge === "donation"
                                  //                       ? values?.values
                                  //                         .map((item) => {
                                  //                           return item?.total_amount > 0 &&
                                  //                             item?.Ledger &&
                                  //                             item?.payment_date &&
                                  //                             values?.account_name
                                  //                             ? false
                                  //                             : true;
                                  //                         })
                                  //                         .includes(true)
                                  //                       : values?.creditCardAmount
                                  //                         ? values?.cardDetail
                                  //                           ? values?.cvc
                                  //                             ? false
                                  //                             : true
                                  //                           : true
                                  //                         : values?.onAccountCredit_payment
                                  //                           ? false
                                  //                           : Object.entries(values)
                                  //                             .filter(([key]) => key.includes("_payment"))
                                  //                             .some(([, value]) => !!value)
                                  //                             ? false
                                  //                             : true
                                  //       : false)
                                }
                              />
                            )}
                          {HidePaymentButton === true &&
                            values.values?.[0]?.isRecurring &&
                            values.type === "edit-charge" &&
                            currentStepIndex === 0 && (
                              <MyButton
                                style={{ marginRight: 10, height: 40 }}
                                type="secondary"
                                content={
                                  <span>
                                    <span>
                                      {hfinish ? "Cancel " : "Save transaction"}
                                    </span>
                                  </span>
                                }
                                // disabled={loading}
                                // disable={loading}
                                onClick={() => {
                                  setBtnContent("");
                                  if (hfinish) {
                                    onClose();
                                    return;
                                  }
                                  if (
                                    currentStepIndex === 0 &&
                                    !validateStepOneRequiredFields(values)
                                  ) {
                                    return;
                                  }
                                  saveTransaction(
                                    {
                                      ...values,
                                      transactions: values.values,
                                    },
                                    setFieldValue,
                                    currentStepIndex
                                  );
                                }}
                                disabled={loading}
                              />
                            )}

                          {
                            // values?.values?.some(rect => rect.is_splitted === true) ? currentStepIndex === 0 : ""
                            isAnySplit &&
                              currentStepIndex === 0 ? (
                              ""
                            ) :
                              HidePaymentButton === true &&
                                currentStepIndex === 0 &&
                                !!values?.values?.[0]?.isRecurring ? (
                                ""
                              ) : (
                                // values.values?.[0]?.isRecurring && values.type === "edit-charge" && currentStepIndex === 0 ? "" :
                                <MyButton
                                  id={
                                    currentStepIndex !== 2
                                      ? `Next: ${currentStepIndex === 0
                                        ? "Payment information"
                                        : "Review"
                                      }`
                                      : "Pay Now"
                                  }
                                  style={{ paddingInline: 40, ...(currentStepIndex === 0 ? { height: 40 } : {}) }}
                                  type={
                                    currentStepIndex === 1 ? "primary" : "primary"
                                  }
                                  content={
                                    <p
                                      onClick={(e) => {
                                        setBtnContent(e.target.id);
                                      }}
                                      id={
                                        currentStepIndex !== 2
                                          ? `Next: ${currentStepIndex === 0
                                            ? "Payment information"
                                            : "Review"
                                          }`
                                          : "Pay Now"
                                      }
                                    >
                                      {currentStepIndex !== 2
                                        ? `Next: ${currentStepIndex === 0
                                          ? "Payment information"
                                          : "Review"
                                        }`
                                        : values?.values?.[0]?.isRecurring &&
                                          hasRecurringEnd(values) &&
                                          moment(
                                            values?.values?.[0]?.recurringStart
                                          ).format("YYYY-MM-DD") ===
                                          moment(new Date()).format("YYYY-MM-DD")
                                          ? "Pay Now"
                                          : values?.values?.[0]?.isRecurring &&
                                            hasRecurringEnd(values) &&
                                            moment(
                                              values?.values?.[0]?.recurringStart
                                            ).format("YYYY-MM-DD") !==
                                            moment(new Date()).format("YYYY-MM-DD")
                                            ? "Create Recurring"
                                            : (() => {
                                                const hasFuture = values?.cc_future_enabled || values?.ach_future_enabled;
                                                const selected = values?.selectedPaymentTitles || [];
                                                const futureTypes = [
                                                  ...(values?.cc_future_enabled ? ["credit card"] : []),
                                                  ...(values?.ach_future_enabled ? ["ach"] : []),
                                                ];
                                                const hasNonFuture = selected.some(t => !futureTypes.includes(t?.toLowerCase()));
                                                if (hasFuture && hasNonFuture) return "Schedule and Pay";
                                                if (hasFuture) return "Schedule Payment";
                                                return "Pay Now";
                                              })()}
                                      {currentStepIndex === 2 && loading && (
                                        <span className="ml-3 spinner spinner-white"></span>
                                      )}
                                    </p>
                                  }
                                  onClick={(e) => {

                                    setBtnContent(e.target.id);

                                    if (currentStepIndex === 2) {
                                      // localStorage.setItem("setRecurring", "false");
                                      if (
                                        values?.values?.[0]?.isRecurring &&
                                        hasRecurringEnd(values) &&
                                        moment(
                                          values?.values?.[0]?.recurringStart
                                        ).format("YYYY-MM-DD") ===
                                        moment(new Date()).format("YYYY-MM-DD")
                                      ) {
                                        onCheckout(values, currentStepIndex);
                                      }
                                      if (
                                        values?.values?.[0]?.isRecurring &&
                                        hasRecurringEnd(values) &&
                                        moment(
                                          values?.values?.[0]?.recurringStart
                                        ).format("YYYY-MM-DD") !==
                                        moment(new Date()).format("YYYY-MM-DD")
                                      ) {
                                        onRecurringPayment(
                                          values,
                                          currentStepIndex
                                        );
                                      }
                                      if (!values?.values?.[0]?.isRecurring) {
                                        onCheckout(values, currentStepIndex);
                                      }
                                    }
                                    else if (currentStepIndex === 0) {
                                      if (values?.values?.[0]?.isRecurring) {
                                        dispatch(SetRecurringChecks("show"));
                                        // localStorage.setItem("setRecurring", "true");
                                      } else {
                                        dispatch(SetRecurringChecks("hide"));
                                        // localStorage.setItem("setRecurring", "false");
                                      }
                                      setInputValues(values);
                                      onStepClick(
                                        values,
                                        handleNext,
                                        setFieldValue,
                                        currentStepIndex,
                                        (dontCall = true)
                                      );
                                    } else {
                                      onStepClick(
                                        values,
                                        handleNext,
                                        setFieldValue,
                                        currentStepIndex
                                      );
                                    }
                                  }}
                                  disabled={
                                    currentStepIndex === 0
                                      ? loading
                                      : values.values?.[0]?.isRecurring
                                      ? false
                                      : values?.actual_total <= 0
                                        ? true
                                        : loading
                                          ? true
                                          : currentStepIndex === 0 && values?.type === "edit-charge" ? (+values?.actual_total - (+values?.totalPaid || 0)) <= 0
                                            : currentStepIndex === 0 && values?.type === "edit-charge" ? (+values?.actual_total - +values?.current_amount) <= 0 &&
                                              values?.amount_due <= 0
                                              : data && Object.keys(data).length !== 0
                                                ? false
                                                : loading ||
                                                (currentStepIndex !== 2
                                                  ?
                                                  currentStepIndex === 0 &&
                                                    (values?.type_of_charge === "charge" ||
                                                      values?.type_of_charge ===
                                                      "credit_on_account") &&
                                                    values?.require_notes === true
                                                    ? values?.values
                                                      .map((item) => {
                                                        return item?.total_amount > 0 &&
                                                          item?.Ledger &&
                                                          item?.payment_date &&
                                                          values?.account_name?.id &&
                                                          item?.Ledger?.account_receivable
                                                          ? // &&
                                                          // item?.note !== "" &&
                                                          // item?.note?.match(
                                                          //   /^(?![\s-])[\w\s-]+$/,
                                                          //   "Enter a valid name"
                                                          // )
                                                          false
                                                          : true;
                                                      })
                                                      .includes(true)
                                                    : currentStepIndex === 0 &&
                                                      (values?.type_of_charge ===
                                                        "charge" ||
                                                        values?.type_of_charge ===
                                                        "credit_on_account") &&
                                                      values?.require_notes === false
                                                      ? values?.values
                                                        .map((item) => {
                                                          return item?.Ledger &&
                                                            item?.total_amount > 0 &&
                                                            item?.payment_date &&
                                                            values?.account_name?.id
                                                            ? // &&
                                                            // item?.note !== "" &&
                                                            // item?.note?.match(
                                                            //   /^(?![\s-])[\w\s-]+$/,
                                                            //   "Enter a valid name"
                                                            // )
                                                            false
                                                            : true;
                                                        })
                                                        .includes(true)
                                                      : currentStepIndex === 0 &&
                                                        (values?.type_of_charge ===
                                                          "charge" ||
                                                          values?.type_of_charge ===
                                                          "credit_on_account") &&
                                                        values?.require_notes === true
                                                        ? values?.values
                                                          .map((item) => {
                                                            return item?.total_amount > 0 &&
                                                              item?.Ledger &&
                                                              item?.payment_date &&
                                                              values?.account_name?.id &&
                                                              item?.Ledger?.account_receivable
                                                              ? // &&
                                                              // item?.note !== "" &&
                                                              // item?.note?.match(
                                                              //   /^(?![\s-])[\w\s-]+$/,
                                                              //   "Enter a valid name"
                                                              // )
                                                              false
                                                              : true;
                                                          })
                                                          .includes(true)
                                                        : currentStepIndex === 0 &&
                                                          (values?.type_of_charge ===
                                                            "charge" ||
                                                            values?.type_of_charge ===
                                                            "credit_on_account") &&
                                                          values?.require_notes === false
                                                          ? values?.values
                                                            .map((item) => {
                                                              return item?.Ledger &&
                                                                item?.total_amount > 0 &&
                                                                item?.payment_date &&
                                                                values?.account_name?.id
                                                                ? //  &&
                                                                // item?.note !== "" &&
                                                                // item?.note?.match(
                                                                //   /^(?![\s-])[\w\s-]+$/,
                                                                //   "Enter a valid name"
                                                                // )
                                                                false
                                                                : true;
                                                            })
                                                            .includes(true)
                                                          : currentStepIndex === 0 &&
                                                            (values?.type_of_charge ===
                                                              "charge" ||
                                                              values?.type_of_charge ===
                                                              "credit_on_account") &&
                                                            values.type === "pay-now"
                                                            ? values?.values
                                                              .map((item) => {
                                                                return item?.Ledger &&
                                                                  item?.total_amount > 0 &&
                                                                  item?.payment_date &&
                                                                  values?.account_name?.id
                                                                  ? // &&
                                                                  // item?.note !== "" &&
                                                                  // item?.note?.match(
                                                                  //   /^(?![\s-])[\w\s-]+$/,
                                                                  //   "Enter a valid name"
                                                                  // )
                                                                  false
                                                                  : true;
                                                              })
                                                              .includes(true)
                                                            : values?.cash_payment
                                                              ? false
                                                              : values?.selectedACH
                                                                ? values?.ach
                                                                  ? false
                                                                  : true
                                                                : values?.checks.length !== 0
                                                                  ? values?.checks
                                                                    .map((item) => {
                                                                      return item.amount &&
                                                                        item.check_date &&
                                                                        item?.check_number
                                                                        ? false
                                                                        : true;
                                                                    })
                                                                    .includes(true)
                                                                  : currentStepIndex === 0 &&
                                                                    (values?.type_of_charge ===
                                                                      "donation" ||
                                                                      values.values[0].charge_type ===
                                                                      "donation")
                                                                    ? values?.values
                                                                      .map((item) => {
                                                                        return item?.Ledger &&
                                                                          item?.total_amount > 0 &&
                                                                          item?.payment_date &&
                                                                          (values?.account_name?.id || accountTransaction?._id)
                                                                          ? false
                                                                          : true;
                                                                      })
                                                                      .includes(true)
                                                                    : values?.creditCardAmount
                                                                      ? values?.cardDetail
                                                                        ? values?.cvc
                                                                          ? false
                                                                          : false
                                                                        : false
                                                                      : values?.onAccountCredit_payment
                                                                        ? false
                                                                        : Object.entries(values)
                                                                          .filter(([key]) =>
                                                                            key.includes("_payment")
                                                                          )
                                                                          .some(([, value]) => !!value)
                                                                          ? false
                                                                          : true
                                                  : false)
                                  }
                                />
                              )}
                        </div>
                      </div>,
                      footerPortalRef.current
                      )}
                    </div>
                  )}
                </div>
              </div>
              {openPaymentModal &&
                <NewDialog
                  isOpen={openPaymentModal}
                  handleClose={() => {
                    confirmPayment.current = false;
                    setOpenPaymentModal(false);
                  }}
                  content={<p
                    style={{
                      textTransform: "none"
                    }}
                  >The payment amount is higher than the selected charges balance, a credit on account of <strong>{currency}{totalPaidAmount} </strong>will be added to the account.</p>}
                >
                  <div className="payment-modal">
                    <button
                      className="payment-modal-btn"
                      onClick={() => {
                        setOpenPaymentModal(false);
                        handlePrev();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="payment-modal-btn payment-modal-btn-primary"
                      onClick={() => {
                        confirmPayment.current = true;
                        onCheckout(values, currentStepIndex);
                        setOpenPaymentModal(false);
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </NewDialog>
              }
              {openConfirmNewPaymentModal &&
                <NewDialog
                  isOpen={openConfirmNewPaymentModal}
                  handleClose={() => {
                    confirmPayment.current = false;
                    setOpenConfirmNewPaymentModal(false);
                  }}
                  content={<p
                    style={{
                      textTransform: "none"
                    }}
                  >This charge is currently associated with an active payment plan. Click ‘Pay’ to cancel the existing payment plan and proceed with a new payment.</p>}
                >
                  <div className="payment-modal">
                    <button
                      className="payment-modal-btn"
                      onClick={() => {
                        setOpenConfirmNewPaymentModal(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="payment-modal-btn payment-modal-btn-primary"
                      onClick={() => {
                        confirmPayment.current = true;
                        onCheckout(values, currentStepIndex);
                        setOpenConfirmNewPaymentModal(false);
                      }}
                    >
                      Pay
                    </button>
                  </div>
                </NewDialog>
              }
            </div>
          );
        }}
      </FormikWizard>
      <AlertDialog
        loading={loading}
        disabled={loading}
        open={openModalDialog}
        handleClose={() => {
          setOpenModalDialog(false);
          dispatch(RecurringModelCheck(false));
        }}
        onDecline={() => {
          setOpenModalDialog(false);
          dispatch(RecurringModelCheck(false));
        }}
        onAccept={() => {
          handleSubmit();
        }}
        title={"Are you sure?"}
        description={
          "Deleting this transaction is non-reversible. would you like to proceed?"
        }
      />

    </div>
  );
};
export default NewCharge;